import { NgTemplateOutlet } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChild,
    ElementRef,
    DestroyRef,
    effect,
    inject,
    input,
    linkedSignal,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import type { MatMenuTrigger } from '@angular/material/menu';
import { MatMenuModule } from '@angular/material/menu';
import { NgIcon } from '@ng-icons/core';
import { injectUser } from '@quipex/core/auth';
import { injectViewportBreakpoints } from '@quipex/core/utils';
import {
    QpxButton,
    QpxCheckbox,
    DialogHeader,
    QpxHeader,
    QpxIcon,
    QpxTypography,
    QpxContentWidthContainer,
} from '@quipex/shared/ui';
import {
    QuippyBuildingSelectorSlot,
    QuippyQuestionBox,
    type QuippyScopeRowPosition,
} from '../../composer';
import {
    injectQuippySession,
    type QuippyAssistantAppearance,
    type QuippyChatRef,
    type QuippyDocumentOption,
    type QuippyMessage,
    type QuippyScope,
} from '../../runtime';
import {
    injectQuippySuggestions,
    prepareQuippySuggestionDraft,
    QuippySuggestedQuestions,
    type QuippySuggestedQuestion,
} from '../../suggestions';
import { observeResponsiveSuggestions } from '../suggestion-row-count';
import {
    createQuippyAssistantUiState,
    quippyScopeRowIdentity,
} from '../ui-state';
import { QuippyChatHistory } from '../ui/chat-history';
import { QuippyThread } from '../ui/thread';
import { injectDialogSuggestionSizing } from './dialog-suggestion-sizing';
import { QuippyOutcomeCards } from './outcome-cards/outcome-cards';
import { injectQuippyOutcomeSelection } from './outcome-selection';
import { getQuippyAssistantCopy } from './quippy-assistant-copy';

export type QuippyAssistantHeader = 'dialog' | 'page';

let nextAssistantId = 0;

@Component({
    selector: 'qpx-quippy-assistant',
    imports: [
        NgTemplateOutlet,
        MatButtonModule,
        MatCheckboxModule,
        MatMenuModule,
        NgIcon,
        QpxButton,
        QpxCheckbox,
        DialogHeader,
        QpxHeader,
        QpxIcon,
        QpxTypography,
        QuippyChatHistory,
        QuippyQuestionBox,
        QuippySuggestedQuestions,
        QuippyThread,
        QuippyOutcomeCards,
    ],
    templateUrl: './quippy-assistant.html',
    styleUrls: ['./quippy-assistant.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'panel-main p-main pb-0!',
        '[class.open]': 'isOpen()',
        '[class.off]': '!isOpen()',
        '[style.--qpx-insights-container-height.px]': 'chatHeight()',
    },
    hostDirectives: [QpxContentWidthContainer],
})
export class QuippyAssistant {
    private readonly viewport = injectViewportBreakpoints();
    protected readonly isPhone = this.viewport.isPhone;
    //#region Dependency injection
    private readonly destroyRef = inject(DestroyRef);
    private readonly session = injectQuippySession();
    private readonly me = injectUser();
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef, {
        host: true,
    });
    //#endregion

    //#region Inputs
    public readonly isOpen = input.required<boolean>();
    public readonly startWithPack = input(false);
    public readonly quippyScope = input.required<QuippyScope>();
    public readonly chatRef = input.required<QuippyChatRef>();
    public readonly appearance = input<QuippyAssistantAppearance>('elevated');
    public readonly header = input<QuippyAssistantHeader>('page');
    //#endregion

    //#region Outputs
    public readonly closeChat = output<void>();
    public readonly newChat = output<void>();
    public readonly messageSent = output<QuippyChatRef>();
    public readonly selectChat = output<QuippyChatRef>();
    public readonly openPage = output<QuippyChatRef>();
    public readonly openDialog = output<QuippyChatRef>();
    //#endregion

    //#region State
    private readonly uiState = createQuippyAssistantUiState({
        session: this.session,
        chatRef: this.chatRef,
        isOpen: this.isOpen,
    });
    protected readonly currentChatId = this.uiState.currentChatId;
    protected readonly messages = this.uiState.messages;
    protected readonly draft = this.uiState.draft;
    protected readonly sidebarThreads = this.uiState.sidebarThreads;
    protected readonly showSuggestions = this.uiState.showSuggestions;
    protected readonly hasSentMessage = this.uiState.hasSentMessage;
    protected readonly isLoading = this.uiState.isLoading;
    protected readonly suggestionPageSize = signal(4);
    protected readonly chatHeight = signal<number | null>(null);
    protected readonly isAdminModeEnabled = signal(false);
    private readonly domId = `quippy-assistant-${nextAssistantId++}`;
    protected readonly titleId = `${this.domId}-title`;
    protected readonly adminModeId = `${this.domId}-admin-mode`;
    protected readonly inputHintId = `${this.domId}-input-hint`;
    protected readonly canUseAdminMode = computed(
        () => this.me()?.isSysAdmin === true
    );
    protected readonly assistantCopy = computed(() =>
        getQuippyAssistantCopy(this.quippyScope().kind)
    );
    protected readonly scopeSelector = contentChild(QuippyBuildingSelectorSlot);
    protected readonly scopeRowDismissed = linkedSignal<string, boolean>({
        source: () =>
            quippyScopeRowIdentity(this.chatRef(), this.quippyScope()),
        computation: (identity, previous) =>
            previous?.source === identity ? previous.value : false,
    });
    protected readonly promptSuggestions = injectQuippySuggestions(
        this.quippyScope
    );
    protected readonly outcomeSelection = injectQuippyOutcomeSelection({
        startWithPack: this.startWithPack,
        isAdmin: this.isAdminModeEnabled,
        identity: () =>
            `${quippyScopeRowIdentity(this.chatRef(), this.quippyScope())}:${this.isAdminModeEnabled()}`,
        userType: () => this.me()?.userType,
        scope: this.quippyScope,
        questions: this.promptSuggestions,
    });
    protected readonly scopeRowPosition = computed<QuippyScopeRowPosition>(
        () => {
            if (this.hasSentMessage()) return 'above';
            return this.scopeSelector() ? 'above' : 'hidden';
        }
    );
    protected readonly sourceFallbackBuildingId = computed(() => {
        const scope = this.quippyScope();
        return scope.kind === 'building' ? scope.buildingId : null;
    });
    //#endregion

    //#region Template references
    protected readonly chatHistoryMenuTrigger = viewChild<MatMenuTrigger>(
        'chatHistoryMenuTrigger'
    );
    private readonly chatHistory = viewChild<QuippyChatHistory>('chatHistory');
    private readonly suggestionsPane =
        viewChild<ElementRef<HTMLElement>>('suggestionsPane');
    private readonly composer = viewChild<ElementRef<HTMLElement>>('composer');
    private readonly questionBox = viewChild<QuippyQuestionBox>('questionBox');
    private readonly shouldRestorePromptFocus = signal(false);
    //#endregion

    //#region Lifecycle hooks
    constructor() {
        afterNextRender(() => {
            observeResponsiveSuggestions(this.elementRef.nativeElement, {
                enabled: () => this.header() !== 'dialog',
                appearance: this.appearance,
                count: () => this.promptSuggestions().length,
                pageSize: this.suggestionPageSize,
                updateHeight: () => this.updateChatHeight(),
                destroyRef: this.destroyRef,
            });
            if (this.isOpen()) this.questionBox()?.focusPrompt();
        });
        injectDialogSuggestionSizing({
            pane: this.suggestionsPane,
            composer: this.composer,
            host: this.elementRef.nativeElement,
            header: this.header,
            pageSize: this.suggestionPageSize,
        });
        effect(() => {
            if (this.isOpen()) {
                this.updateChatHeight();
            }
        });
        effect(() => {
            if (!this.canUseAdminMode()) {
                this.isAdminModeEnabled.set(false);
            }
        });
        effect(() => {
            if (!this.isOpen()) {
                this.shouldRestorePromptFocus.set(false);
                return;
            }

            if (!this.shouldRestorePromptFocus() || this.isLoading()) return;

            this.questionBox()?.focusPrompt();
            this.shouldRestorePromptFocus.set(false);
        });
    }
    //#endregion

    //#region Template methods
    protected onInput(value: string): void {
        this.session.setDraft(this.chatRef(), value);
    }

    protected onQuestionSubmitted(question: string): void {
        const draft = question.trim();
        if (!draft || this.isLoading()) return;

        this.shouldRestorePromptFocus.set(true);
        this.session.send(this.chatRef(), draft, this.isAdminModeEnabled());
        this.messageSent.emit(this.chatRef());
    }

    protected onPromptSelected(prompt: QuippySuggestedQuestion): void {
        if (prompt.action === 'submit') {
            this.session.send(
                this.chatRef(),
                prompt.text,
                this.isAdminModeEnabled()
            );
            this.messageSent.emit(this.chatRef());
            return;
        }

        this.session.setDraft(
            this.chatRef(),
            prepareQuippySuggestionDraft(prompt.text)
        );
        this.questionBox()?.focusPrompt();
    }

    protected onRetry(message: QuippyMessage): void {
        this.session.retry(
            this.chatRef(),
            message.id,
            this.isAdminModeEnabled()
        );
    }

    protected onEditResend(event: {
        message: QuippyMessage;
        content: string;
    }): void {
        this.session.edit(
            this.chatRef(),
            event.message.id,
            event.content,
            this.isAdminModeEnabled()
        );
        this.shouldRestorePromptFocus.set(true);
    }

    protected onAdminModeChanged(checked: boolean): void {
        this.isAdminModeEnabled.set(this.canUseAdminMode() && checked);
    }

    protected onDocumentOptionSelected(event: {
        message: QuippyMessage;
        option: QuippyDocumentOption;
    }): void {
        this.session.selectDocument(
            this.chatRef(),
            event.message.id,
            event.option.fileId
        );
    }

    protected onChatHistoryOpened(): void {
        queueMicrotask(() => this.chatHistory()?.focusFirstItem());
    }

    protected onSwitchThread(ref: QuippyChatRef): void {
        this.selectChat.emit(ref);
        this.chatHistoryMenuTrigger()?.closeMenu();
    }

    protected onOpenInPage(): void {
        this.openPage.emit(this.chatRef());
    }

    protected onOpenInDialog(): void {
        this.openDialog.emit(this.chatRef());
    }

    //#endregion

    //#region Private methods
    private updateChatHeight(): void {
        this.chatHeight.set(this.elementRef.nativeElement.clientHeight);
    }
    //#endregion
}
